import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useSelector } from "react-redux";
import firebase from "../../config/firebase";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import { cardIconSx } from "../../theme/globalStyle/globalStyle";
import AlignVerticalBottomIcon from "@mui/icons-material/AlignVerticalBottom";
import PlusOneIcon from "@mui/icons-material/PlusOne";

const defaultConfig = {
  grissAdvalorem: "0,3",
  administrationFee: "5",
  workingCapitalFee: "3",
  otherExpenses: "0",
  federalTaxes: "7",
};

const fieldDefs = [
  {
    key: "grissAdvalorem",
    label: "GRISS/Advalorem",
    helper: "Percentual padrao: 0,3%",
  },
  {
    key: "administrationFee",
    label: "Taxa de Administracao",
    helper: "Percentual padrao: 5%",
  },
  {
    key: "workingCapitalFee",
    label: "Taxa de Capital de Giro",
    helper: "Percentual padrao: 3%",
  },
  {
    key: "otherExpenses",
    label: "Outras Despesas",
    helper: "Percentual padrao: 0%",
  },
  {
    key: "federalTaxes",
    label: "Impostos Federais",
    helper: "Percentual padrao: 7%",
  },
];

const db = firebase.firestore();

const toNumber = (value) => {
  const parsed = Number(String(value || "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatPercent = (value) =>
  `${value.toLocaleString("pt-BR", {
    minimumFractionDigits: value % 1 ? 1 : 0,
    maximumFractionDigits: 2,
  })}%`;

const sanitizePercentInput = (value) => {
  let next = String(value || "")
    .replace(/[^\d.,]/g, "")
    .replace(".", ",");
  const parts = next.split(",");
  if (parts.length > 2) {
    next = `${parts[0]},${parts.slice(1).join("")}`;
  }

  const [intPart = "", decPart = ""] = next.split(",");
  const normalizedInt = intPart.slice(0, 3);
  const normalizedDec = decPart.slice(0, 2);

  return normalizedDec.length > 0
    ? `${normalizedInt},${normalizedDec}`
    : normalizedInt;
};

const mapFinancialToForm = (financial) => ({
  grissAdvalorem: sanitizePercentInput(
    financial?.grissAdvalorem ?? defaultConfig.grissAdvalorem,
  ),
  administrationFee: sanitizePercentInput(
    financial?.administrationFee ?? defaultConfig.administrationFee,
  ),
  workingCapitalFee: sanitizePercentInput(
    financial?.workingCapitalFee ?? defaultConfig.workingCapitalFee,
  ),
  otherExpenses: sanitizePercentInput(
    financial?.otherExpenses ?? defaultConfig.otherExpenses,
  ),
  federalTaxes: sanitizePercentInput(
    financial?.federalTaxes ?? defaultConfig.federalTaxes,
  ),
});

const mapFormToFinancial = (form) => ({
  grissAdvalorem: sanitizePercentInput(form.grissAdvalorem),
  administrationFee: sanitizePercentInput(form.administrationFee),
  workingCapitalFee: sanitizePercentInput(form.workingCapitalFee),
  otherExpenses: sanitizePercentInput(form.otherExpenses),
  federalTaxes: sanitizePercentInput(form.federalTaxes),
});

export default function RevenuePage() {
  const user = useSelector((state) => state.user);
  const shipperId = user?.uidShipper || null;

  const [form, setForm] = useState(defaultConfig);
  const [saved, setSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const totalPercent = useMemo(
    () => fieldDefs.reduce((acc, field) => acc + toNumber(form[field.key]), 0),
    [form],
  );

  const estimatedFactor = useMemo(() => 1 + totalPercent / 100, [totalPercent]);

  const loadFinancialConfig = useCallback(async () => {
    if (!shipperId) {
      setLoading(false);
      setErrorMessage("Usuario sem uidShipper vinculado.");
      return;
    }

    setLoading(true);

    try {
      const snapshot = await db
        .collection("revenue")
        .where("uidShipper", "==", shipperId)
        .get();
      const resultSnapshotRevenue = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))[0];

      if (!resultSnapshotRevenue.id) {
        setForm(defaultConfig);
        setErrorMessage("Faturamento não encontrado.");
        return;
      }

      setForm(mapFinancialToForm(resultSnapshotRevenue));
    } catch (error) {
      console.log("Erro ao carregar parametros de faturamento:", error);
      setErrorMessage(
        "Nao foi possivel carregar os parametros de faturamento.",
      );
    } finally {
      setLoading(false);
    }
  }, [shipperId]);

  useEffect(() => {
    loadFinancialConfig();
  }, [loadFinancialConfig]);

  const handleChange = (key) => (event) => {
    setForm((prev) => ({
      ...prev,
      [key]: sanitizePercentInput(event.target.value),
    }));
  };

  const handleReset = () => {
    setForm(defaultConfig);
  };

  const handleSave = async () => {
    if (!shipperId) {
      setErrorMessage("Usuario sem uidShipper vinculado.");
      return;
    }

    setSaving(true);

    try {
      const snapshot = await db
        .collection("revenue")
        .where("uidShipper", "==", shipperId)
        .get();
      const currentData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))[0];

      const currentFinancial = currentData?.financial || {};

      const nextFinancial = {
        ...currentFinancial,
        ...mapFormToFinancial(form),
        updatedAtRevenue: new Date(),
        uidShipper: shipperId,
      };

      if (!currentData) {
        await db.collection("revenue").add(nextFinancial);
      } else {
        await db
          .collection("revenue")
          .doc(currentData.id)
          .update(nextFinancial, { merge: true });
      }

      setSaved(true);
    } catch (error) {
      console.log("Erro ao salvar parametros de faturamento:", error);
      setErrorMessage("Nao foi possivel salvar os parametros de faturamento.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <NewMiniDrawer
      divOpen={
        <Box sx={pageWrapperSx}>
          <Card sx={heroCardSx}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={cardIconSx}>
                    <PointOfSaleIcon sx={{ color: "#ffffff" }} />
                  </Box>
                  <Typography sx={heroTitleSx}>Faturamento</Typography>
                </Box>
                <Typography sx={heroSubtitleSx}>
                  Configure os percentuais base da politica de faturamento e
                  mantenha um padrao operacional para simulacao e precificacao.
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Grid container spacing={2.2}>
            <Grid item xs={12} md={8}>
              <Card sx={contentCardSx}>
                <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Box sx={cardIconSx}>
                      <AlignVerticalBottomIcon sx={{ color: "#ffffff" }} />
                    </Box>
                    <Typography sx={heroTitleSx}>
                      Parametros de Taxas
                    </Typography>
                  </Box>
                  <Grid container spacing={1.8}>
                    {fieldDefs.map((field) => (
                      <Grid item xs={12} sm={6} key={field.key}>
                        <Stack spacing={0.55}>
                          <Typography sx={fieldLabelSx}>
                            {field.label}
                          </Typography>
                          <TextField
                            value={form[field.key]}
                            onChange={handleChange(field.key)}
                            size="small"
                            placeholder="0"
                            fullWidth
                            disabled={loading || saving}
                            sx={percentInputSx}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  %
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Typography sx={helperSx}>{field.helper}</Typography>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>

                  <Divider
                    sx={{ my: 2, borderColor: "rgba(1, 36, 67, 0.14)" }}
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                    <Button
                      variant="outlined"
                      startIcon={<RestartAltOutlinedIcon />}
                      onClick={handleReset}
                      disabled={loading || saving}
                      sx={secondaryActionSx}
                    >
                      Restaurar padroes
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SaveOutlinedIcon />}
                      onClick={handleSave}
                      disabled={loading || saving}
                      sx={primaryActionSx}
                    >
                      {saving ? "Salvando..." : "Salvar"}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={summaryCardSx}>
                <CardContent sx={{ p: { xs: 2, md: 2.25 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Box sx={cardIconSx}>
                      <PlusOneIcon sx={{ color: "#ffffff" }} />
                    </Box>
                    <Typography sx={heroTitleSx}>Somatorio</Typography>
                  </Box>

                  <Stack spacing={1}>
                    {fieldDefs.map((field) => (
                      <Box key={`summary-${field.key}`} sx={summaryRowSx}>
                        <Typography sx={summaryLabelSx}>
                          {field.label}
                        </Typography>
                        <Typography sx={summaryValueSx}>
                          {formatPercent(toNumber(form[field.key]))}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  <Divider
                    sx={{ my: 1.6, borderColor: "rgba(1, 36, 67, 0.14)" }}
                  />

                  <Box sx={totalBoxSx}>
                    <Typography sx={totalLabelSx}>
                      Carga total de taxas
                    </Typography>
                    <Typography sx={totalValueSx}>
                      {formatPercent(totalPercent)}
                    </Typography>
                  </Box>

                  <Typography sx={formulaSx}>
                    Fator aplicado sobre base de calculo:{" "}
                    {estimatedFactor.toFixed(3)}x
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Backdrop
            open={loading || saving}
            sx={{ color: "#fff", zIndex: 9999 }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          <Snackbar
            open={saved}
            autoHideDuration={2500}
            onClose={() => setSaved(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              severity="success"
              variant="filled"
              onClose={() => setSaved(false)}
            >
              Parametros de faturamento salvos com sucesso.
            </Alert>
          </Snackbar>

          <Snackbar
            open={Boolean(errorMessage)}
            autoHideDuration={4000}
            onClose={() => setErrorMessage("")}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity="error"
              variant="filled"
              onClose={() => setErrorMessage("")}
            >
              {errorMessage}
            </Alert>
          </Snackbar>
        </Box>
      }
    />
  );
}

const pageWrapperSx = {
  p: { xs: 2, md: 3 },

  display: "flex",
  flexDirection: "column",

  gap: 2,
};

const heroCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(1, 36, 67, 0.12)",
  boxShadow: "0 14px 28px rgba(1, 36, 66, 0.10)",
};

const heroTitleSx = {
  color: "#012443",
  fontWeight: 700,
  fontSize: "1.25rem",
};

const heroSubtitleSx = {
  color: "rgba(1, 36, 67, 0.74)",
  fontSize: "0.92rem",
  lineHeight: 1.5,
  maxWidth: 800,
};

const contentCardSx = {
  borderRadius: "16px",
  border: "1px solid rgba(1, 36, 67, 0.12)",
  boxShadow: "0 12px 24px rgba(1, 36, 66, 0.08)",
};

const summaryCardSx = {
  borderRadius: "16px",
  border: "1px solid #0124431F",
  boxShadow: "0 12px 24px rgba(1, 36, 66, 0.08)",
  background: "rgba(250, 252, 255, 0.9)",
};

const fieldLabelSx = {
  color: "rgba(1, 36, 67, 0.86)",
  fontSize: "0.84rem",
  fontWeight: 600,
};

const helperSx = {
  color: "rgba(1, 36, 67, 0.56)",
  fontSize: "0.75rem",
};

const percentInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontSize: 14,
    height: "40px",
  },
  "& .MuiInputBase-input": {
    fontSize: 14,
  },
};

const primaryActionSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "10px",
  backgroundColor: "#012443",
  "&:hover": {
    backgroundColor: "#001d35",
  },
};

const secondaryActionSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "10px",
  borderColor: "rgba(1, 36, 67, 0.28)",
  color: "#012443",
};

const summaryRowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  p: 1,
  border: "1px solid rgba(1, 36, 67, 0.10)",
  borderRadius: "10px",
  backgroundColor: "#fff",
  gap: 1,
};

const summaryLabelSx = {
  color: "rgba(15, 23, 42, 0.9)",
  fontSize: "0.82rem",
  fontWeight: 500,
};

const summaryValueSx = {
  color: "#012443",
  fontSize: "0.86rem",
  fontWeight: 700,
};

const totalBoxSx = {
  p: 1.2,
  borderRadius: "12px",
  border: "1px solid rgba(1, 36, 67, 0.24)",
  background:
    "linear-gradient(180deg, rgba(241,248,255,1) 0%, rgba(233,244,255,0.9) 100%)",
};

const totalLabelSx = {
  color: "rgba(1, 36, 67, 0.78)",
  fontWeight: 600,
  fontSize: "0.8rem",
};

const totalValueSx = {
  color: "#012443",
  fontWeight: 800,
  fontSize: "1.15rem",
};

const formulaSx = {
  mt: 1,
  color: "rgba(1, 36, 67, 0.68)",
  fontSize: "0.79rem",
};
